import { useEffect, useState, useRef, useCallback } from "react";
import { useGetSubSchoolDropDownList } from "../../../../hooks/organization/useGetSubSchoolDropDownList";
import { useTenantAuth } from "../../../../context/TeanantAuthProvider";
import "./SubSchoolSelector.css";

const ITEMS_PER_PAGE = 15;

/**
 * @typedef {object} SubSchoolSelectorProps
 * @property {number[]} selectedIds - Currently selected sub-school IDs
 * @property {(ids: number[]) => void} onChange - Callback when selection changes
 * @property {boolean} [disabled] - Whether the selector is disabled
 */

/**
 * A reusable component to select sub-schools (All or Specific)
 * Supports search and infinite scroll
 * @param {SubSchoolSelectorProps} props
 */
const SubSchoolSelector = ({ selectedIds, onChange, disabled = false }) => {
    const { currentUser } = useTenantAuth();
    const {
        subSchools,
        meta,
        loading,
        loadingMore,
        hasMore,
        error,
        getSubSchoolDropDownList,
    } = useGetSubSchoolDropDownList();

    const [sendTo, setSendTo] = useState("all"); // "all" or "specific"
    const [searchQuery, setSearchQuery] = useState("");
    const searchTimeoutRef = useRef(null);
    const listRef = useRef(null);

    // Fetch sub-schools on mount
    useEffect(() => {
        getSubSchoolDropDownList({
            orgId: currentUser?.school_id,
            per_page: ITEMS_PER_PAGE,
        });
    }, []);

    // When "all" is selected, send all IDs
    useEffect(() => {
        if (sendTo === "all" && subSchools && subSchools.length > 0 && !searchQuery) {
            const allIds = subSchools.map((school) => school.id);
            onChange(allIds);
        }
    }, [sendTo, subSchools]);

    // Handle search with debounce
    const handleSearch = (value) => {
        setSearchQuery(value);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            getSubSchoolDropDownList({
                orgId: currentUser?.school_id,
                page: 1,
                per_page: ITEMS_PER_PAGE,
                search: value || undefined,
            });
        }, 400);
    };

    // Infinite scroll handler
    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el || loadingMore || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = el;
        if (scrollTop + clientHeight >= scrollHeight - 40) {
            const nextPage = (meta?.current_page || 1) + 1;
            getSubSchoolDropDownList({
                orgId: currentUser?.school_id,
                page: nextPage,
                per_page: ITEMS_PER_PAGE,
                search: searchQuery || undefined,
            });
        }
    }, [loadingMore, hasMore, meta, searchQuery]);

    const handleSendToChange = (value) => {
        setSendTo(value);
        if (value === "all" && subSchools) {
            const allIds = subSchools.map((school) => school.id);
            onChange(allIds);
        } else if (value === "specific") {
            onChange([]);
            // Reset search and refetch
            setSearchQuery("");
            getSubSchoolDropDownList({
                orgId: currentUser?.school_id,
                page: 1,
                per_page: ITEMS_PER_PAGE,
            });
        }
    };

    const handleCheckboxChange = (schoolId) => {
        if (disabled) return;
        const isChecked = selectedIds.includes(schoolId);
        if (isChecked) {
            onChange(selectedIds.filter((id) => id !== schoolId));
        } else {
            onChange([...selectedIds, schoolId]);
        }
    };

    const handleSelectAll = () => {
        if (disabled || !subSchools) return;
        const totalCount = meta?.total || subSchools.length;
        if (selectedIds.length === totalCount) {
            onChange([]);
        } else {
            onChange(subSchools.map((school) => school.id));
        }
    };

    if (loading && !subSchools) {
        return (
            <div className="sub-school-selector">
                <label className="sub-school-selector-label">Send To *</label>
                <div className="sub-school-selector-loading">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Loading schools...
                </div>
            </div>
        );
    }

    if (error && !subSchools) {
        return (
            <div className="sub-school-selector">
                <label className="sub-school-selector-label">Send To *</label>
                <div className="sub-school-selector-error">
                    <i className="fa-solid fa-circle-exclamation me-2"></i>
                    {error}
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ms-3"
                        onClick={() =>
                            getSubSchoolDropDownList({
                                orgId: currentUser?.school_id,
                                per_page: ITEMS_PER_PAGE,
                            })
                        }
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="sub-school-selector">
            <label className="sub-school-selector-label">Send To *</label>

            {/* Toggle: All / Specific */}
            <div className="sub-school-selector-toggle">
                <button
                    type="button"
                    className={`toggle-btn ${sendTo === "all" ? "active" : ""}`}
                    onClick={() => handleSendToChange("all")}
                    disabled={disabled}
                >
                    All Schools
                </button>
                <button
                    type="button"
                    className={`toggle-btn ${sendTo === "specific" ? "active" : ""}`}
                    onClick={() => handleSendToChange("specific")}
                    disabled={disabled}
                >
                    Specific Schools
                </button>
            </div>

            {/* School Checkboxes (shown only for "specific") */}
            {sendTo === "specific" && (
                <div className="sub-school-selector-list-wrapper">
                    {/* Search Input */}
                    <div className="sub-school-search">
                        <i className="fa-solid fa-magnifying-glass sub-school-search-icon"></i>
                        <input
                            type="text"
                            className="sub-school-search-input"
                            placeholder="Search schools..."
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            disabled={disabled}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="sub-school-search-clear"
                                onClick={() => handleSearch("")}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        )}
                    </div>

                    <div
                        className="sub-school-selector-list"
                        ref={listRef}
                        onScroll={handleScroll}
                    >
                        {loading && !subSchools ? (
                            <div className="sub-school-selector-loading">
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Loading...
                            </div>
                        ) : !subSchools || subSchools.length === 0 ? (
                            <div className="sub-school-selector-empty">
                                <i className="fa-solid fa-school me-2"></i>
                                {searchQuery
                                    ? "No schools match your search."
                                    : "No schools found in your organization."}
                            </div>
                        ) : (
                            <>
                                {/* Select All checkbox (only when not searching) */}
                                {/* {!searchQuery && (
                                    <>
                                        <div
                                            className="sub-school-checkbox-item select-all"
                                            onClick={handleSelectAll}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    subSchools.length > 0 &&
                                                    selectedIds.length === (meta?.total || subSchools.length)
                                                }
                                                onChange={handleSelectAll}
                                                disabled={disabled}
                                                className="sub-school-checkbox"
                                            />
                                            <span className="sub-school-name select-all-label">
                                                Select All ({meta?.total || subSchools.length})
                                            </span>
                                        </div>

                                        <div className="sub-school-checkbox-divider"></div>
                                    </>
                                )} */}

                                {/* Individual school checkboxes */}
                                {subSchools.map((school) => (
                                    <div
                                        key={school.id}
                                        className={`sub-school-checkbox-item ${selectedIds.includes(school.id) ? "checked" : ""}`}
                                        onClick={() => handleCheckboxChange(school.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(school.id)}
                                            onChange={() => handleCheckboxChange(school.id)}
                                            disabled={disabled}
                                            className="sub-school-checkbox"
                                        />
                                        <span className="sub-school-name">{school.name}</span>
                                    </div>
                                ))}

                                {/* Loading more indicator */}
                                {loadingMore && (
                                    <div className="sub-school-loading-more">
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Loading more...
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Selection count - fixed at bottom */}
                    {subSchools && subSchools.length > 0 && (
                        <div className="sub-school-selection-count">
                            {selectedIds.length} school{selectedIds.length !== 1 ? "s" : ""} selected
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SubSchoolSelector;
